import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Theme = 'light' | 'dark' | 'system'

interface ThemeStore {
  theme: Theme
  effectiveTheme: 'light' | 'dark' // The actual theme being used (resolved from system if needed)
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  getSystemTheme: () => 'light' | 'dark'
  applyTheme: () => void
}

// Get system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

// Apply theme to document
const applyThemeToDocument = (theme: 'light' | 'dark') => {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: 'system', // Default to system preference
      effectiveTheme: getSystemTheme(),
      getSystemTheme,
      setTheme: (theme) => {
        const effectiveTheme = theme === 'system' ? getSystemTheme() : theme
        set({ theme, effectiveTheme })
        applyThemeToDocument(effectiveTheme)
      },
      toggleTheme: () => {
        const currentTheme = get().theme
        let newTheme: Theme
        let newEffectiveTheme: 'light' | 'dark'
        
        if (currentTheme === 'system') {
          // If on system, toggle to the opposite of current system preference
          const systemTheme = getSystemTheme()
          newTheme = systemTheme === 'dark' ? 'light' : 'dark'
          newEffectiveTheme = newTheme
        } else if (currentTheme === 'light') {
          newTheme = 'dark'
          newEffectiveTheme = 'dark'
        } else {
          newTheme = 'light'
          newEffectiveTheme = 'light'
        }
        
        set({ theme: newTheme, effectiveTheme: newEffectiveTheme })
        applyThemeToDocument(newEffectiveTheme)
      },
      applyTheme: () => {
        const { theme } = get()
        const effectiveTheme = theme === 'system' ? getSystemTheme() : theme
        set({ effectiveTheme })
        applyThemeToDocument(effectiveTheme)
      },
    }),
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        // Apply theme on rehydration
        if (state) {
          const effectiveTheme = state.theme === 'system' ? getSystemTheme() : state.theme
          state.effectiveTheme = effectiveTheme
          applyThemeToDocument(effectiveTheme)
          
          // Listen for system theme changes if using system preference
          if (state.theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
            const handleChange = () => {
              state?.applyTheme()
            }
            mediaQuery.addEventListener('change', handleChange)
          }
        }
      },
    }
  )
)

// Initialize theme on store creation
if (typeof window !== 'undefined') {
  const store = useThemeStore.getState()
  store.applyTheme()
  
  // Listen for system theme changes if using system preference
  if (store.theme === 'system' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      useThemeStore.getState().applyTheme()
    }
    mediaQuery.addEventListener('change', handleChange)
  }
}

